import { ButtonLink } from '@carouselgg/nexus/button'
import { Callout } from '@carouselgg/nexus/callout'
import { Plus } from '@carouselgg/nexus/icon'
import type { GameTrustScore, TrustScore } from '@carouselgg/auth/types'
import type { TrustStanding } from '../../api/compete'
import { TrustHero } from './TrustHero'
import { TrustSection } from './TrustSection'
import { GameList } from './GameList'
import { TipList } from './TipList'
import { CategoryBreakdown } from './CategoryBreakdown'
import { LowScoreNotice } from './LowScoreNotice'
import { CATEGORY_COPY, CATEGORY_ORDER, biggestFirst, isBaselineCategory } from './trustCopy'
import styles from './TrustPage.module.css'

/**
 * The Carousel score: who you are across every game, every game's own score
 * beside it, how to raise it, and what it is made of.
 */
export function OverallView({ overall, games, scope }: {
  overall: TrustScore
  games: GameTrustScore[]
  /** Which number competitions check right now; null until known. */
  scope: TrustStanding['scope'] | null
}) {
  const tips = biggestFirst(overall.tips)
  const tipTotal = tips.reduce((sum, t) => sum + t.points, 0)
  const total = CATEGORY_ORDER.reduce((sum, k) => sum + (overall.categories[k]?.max ?? 0), 0)

  return (
    <>
      <TrustHero score={overall.score} level={overall.level} eyebrow="Your Carousel score">
        <p>
          Who you are across every game. It is the number on your profile.
          {scope === 'game' && games.length > 0 && <> When you sign up for a competition, it checks <b>your score for that game</b>, shown below.</>}
          {(scope === 'overall' || games.length === 0) && <> Competitions currently check <b>this number</b> when you sign up.</>}
        </p>
      </TrustHero>

      <LowScoreNotice score={overall.score} />

      {games.length > 0 ? (
        <TrustSection
          title="What competitions see"
          aside={`${games.length} ${games.length === 1 ? 'game' : 'games'}`}
          intro={scope === 'overall'
            ? 'Competitions check your Carousel score for now. Each game also keeps its own score: your baseline (identity, social, account age) plus that game\'s history.'
            : 'Your baseline (identity, social, account age) carries into every game. Game history only counts for its own game.'}
        >
          <GameList games={games} overall={overall} />
        </TrustSection>
      ) : (
        <Callout variant="info" icon>
          <div className={styles.calloutBody}>
            <span>
              <b>No games yet.</b> Link a game account and that game gets its own score: your baseline plus your history in it.
            </span>
            <ButtonLink to="/settings/connections" variant="secondary" size="sm"><Plus size={14} aria-hidden="true" /> Add a game</ButtonLink>
          </div>
        </Callout>
      )}

      {tips.length > 0 && (
        <TrustSection title="Raise it" aside={`Biggest gains first · up to ${tipTotal} more points`}>
          <TipList tips={tips} label="Ways to raise your Carousel score" />
        </TrustSection>
      )}

      <TrustSection title={`What makes up your ${overall.score}`} aside={`Six parts, ${total} points in all`}>
        <CategoryBreakdown
          label="Your Carousel score, part by part"
          categories={overall.categories}
          keys={CATEGORY_ORDER}
          copyFor={(key) => ({ ...CATEGORY_COPY[key], tag: isBaselineCategory(key) ? 'Every game' : 'Per game' })}
        />
      </TrustSection>
    </>
  )
}
